import { SyntheticEvent } from 'react';

import { CircularProgress, Autocomplete as MuiAutocomplete } from '@mui/material';

import { ReactComponent as ExpandMoreIcon } from 'assets/icons/ExpandMoreIcon.svg';

import { TextInput } from 'components/_basic/Form/TextInput';

import { IAutocompleteProps } from './types';

import styles from './Autocomplete.module.scss';

export function Autocomplete<IOption>({
  id,
  disabled,
  inputRef,
  label,
  options,
  value,
  fieldError,
  placeholder,
  disableCloseOnSelect,
  multiple,
  freeSolo,
  isLoading,
  name,
  optionValueKey = 'value',
  onChange,
  getOptionLabel,
  getDisabledOptions,
  onFieldClear,
  renderOption,
  groupBy,
  renderGroup,
}: IAutocompleteProps<IOption>) {
  const handleChange = (
    _: SyntheticEvent<Element, Event>,
    newValue: string | IOption | (string | IOption)[] | null,
    reason: string,
  ) => {
    if (onChange) {
      onChange(newValue);
    }

    if (onFieldClear && reason === 'clear') onFieldClear();
  };

  const isOptionEqualToValue = (option: IOption | string, value: IOption | string): boolean => {
    if (typeof option === 'string' || typeof value === 'string') {
      return option === value;
    }

    const key = optionValueKey as keyof IOption;
    return option[key] === value[key];
  };

  const controlledValue = value ? value : multiple ? [] : null;

  return (
    <MuiAutocomplete
      autoHighlight
      disablePortal
      id={id}
      fullWidth
      value={controlledValue}
      options={options}
      multiple={multiple}
      forcePopupIcon
      freeSolo={freeSolo}
      disabled={disabled}
      popupIcon={<ExpandMoreIcon />}
      onInputChange={!multiple && freeSolo ? handleChange : undefined}
      disableCloseOnSelect={disableCloseOnSelect}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionLabel={getOptionLabel}
      getOptionDisabled={getDisabledOptions}
      renderOption={renderOption}
      groupBy={groupBy}
      renderGroup={renderGroup}
      clearIcon={name === 'GLOBAL.currency' ? null : undefined}
      onChange={handleChange}
      loading={isLoading}
      classes={{
        listbox: styles.listbox,
        option: styles.option,
      }}
      renderInput={(params) => (
        <TextInput
          {...params}
          label={label}
          name={name}
          placeholder={placeholder}
          disabled={disabled}
          inputRef={inputRef}
          fieldError={fieldError}
          InputProps={{
            ...params.InputProps,
            className: styles.input,
            name,
            endAdornment: (
              <>
                {isLoading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
